<template>
  <div class="flex-1 flex flex-col md:flex-row min-h-0 overflow-hidden">
    <!-- Left: basic fields -->
    <div class="md:w-[40%] overflow-y-auto p-6 border-b md:border-b-0 md:border-r border-gray-200">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-semibold text-gray-900">Edit Author</h2>
        <button @click="store.closeEditPane()" class="text-gray-500 hover:text-gray-700" title="Close">
          <X :size="20" />
        </button>
      </div>

      <form @submit.prevent="save" class="space-y-4">
        <div>
          <label class="text-sm font-medium text-gray-800 mb-1 block">Name</label>
          <input v-model="form.name" type="text" class="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-teal-500" />
        </div>

        <div>
          <label class="text-sm font-medium text-gray-800 mb-1 block">Writing rules</label>
          <p class="text-sm text-gray-600 mb-1">Plain text. One rule per line.</p>
          <textarea v-model="form.writing_rules" rows="6" placeholder="Use active voice &#x2014; 'The team built' not 'it was built by the team'&#10;Start every section with the key insight, not background&#10;One idea per paragraph, max 4 sentences&#10;Back every claim with a specific example or data point" class="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-teal-500"></textarea>
        </div>

        <div>
          <label class="text-sm font-medium text-gray-800 mb-1 block">Quirks</label>
          <p class="text-sm text-gray-600 mb-1">One per line. Unique style habits.</p>
          <textarea v-model="form.quirks_text" rows="4" placeholder="Opens articles with a bold, contrarian claim&#10;Uses em-dashes instead of parentheses&#10;Ends sections with a one-sentence takeaway in bold&#10;Occasionally references specific books or research by name" class="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-teal-500"></textarea>
        </div>

        <div v-if="error" class="text-sm text-red-600">{{ error }}</div>

        <div class="flex gap-3 pt-2">
          <button type="submit" :disabled="saving" class="px-4 py-2 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 disabled:opacity-50">
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
          <button type="button" @click="store.closeEditPane()" class="px-4 py-2 border border-gray-300 text-sm rounded text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
        </div>
      </form>
    </div>

    <!-- Right: writing examples -->
    <div class="flex-1 overflow-y-auto p-6 bg-gray-50/50">
      <div class="flex items-center justify-between mb-1">
        <label class="text-sm font-medium text-gray-800">Writing examples</label>
        <button @click="addExample" class="text-sm bg-teal-600 text-white rounded px-2 py-1 hover:bg-teal-700">+ Add</button>
      </div>
      <p class="text-sm text-gray-600 mb-3">Paste real writing samples. One per block, 200-500 words each. Auto-saves on blur.</p>

      <div v-if="examples.length === 0" class="text-sm text-gray-600 italic">
        No examples yet.
      </div>
      <div v-for="(ex, idx) in examples" :key="ex.id || ex._tempId" class="mb-3 border border-gray-200 rounded bg-white">
        <div class="flex items-start">
          <textarea
            v-model="ex.content"
            @blur="saveExample(ex)"
            rows="6"
            placeholder="Paste a real writing sample (200-500 words). The AI will match this style, rhythm, and structure..."
            class="flex-1 text-sm text-gray-900 border-0 rounded-l px-3 py-2 focus:outline-none resize-y"
          ></textarea>
          <button @click="deleteExample(ex, idx)" class="px-2 py-2 text-gray-400 hover:text-red-500 flex-shrink-0" title="Delete">
            <Trash2 :size="16" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, inject, onMounted } from 'vue'
import { X, Trash2 } from 'lucide-vue-next'
import { useApi } from '../../composables/useApi'

const store = inject('workbench')
const api = useApi()
const saving = ref(false)
const error = ref(null)

const form = reactive({ name: '', writing_rules: '', quirks_text: '' })

const examples = ref([])
let tempIdCounter = 0

onMounted(async () => {
  const a = store.currentAuthor.value
  if (!a) return
  form.name = a.name || ''
  form.writing_rules = a.writing_rules || ''
  form.quirks_text = (a.quirks || []).join('\n')

  try {
    examples.value = await api.get(`/api/authors/${a.id}/author_examples`)
  } catch (e) {
    console.error('Failed to load examples', e)
  }
})

async function save() {
  if (!store.currentAuthor.value) return
  saving.value = true
  error.value = null
  try {
    const updated = await api.patch(`/api/authors/${store.currentAuthor.value.id}`, {
      author: {
        name: form.name,
        writing_rules: form.writing_rules,
        quirks_text: form.quirks_text,
      }
    })
    store.currentAuthor.value = updated
    await store.loadAllAuthors()
    store.closeEditPane()
  } catch (e) {
    error.value = e.message || 'Failed to save'
  } finally {
    saving.value = false
  }
}

function addExample() {
  examples.value.push({ _tempId: ++tempIdCounter, content: '', _new: true })
}

async function saveExample(ex) {
  const authorId = store.currentAuthor.value?.id
  if (!authorId || !ex.content?.trim()) return
  try {
    if (ex._new || !ex.id) {
      const created = await api.post(`/api/authors/${authorId}/author_examples`, {
        author_example: { title: '—', content: ex.content }
      })
      ex.id = created.id
      ex._new = false
      delete ex._tempId
    } else {
      await api.patch(`/api/authors/${authorId}/author_examples/${ex.id}`, {
        author_example: { content: ex.content }
      })
    }
  } catch (e) {
    console.error('Failed to save example', e)
  }
}

async function deleteExample(ex, idx) {
  const authorId = store.currentAuthor.value?.id
  if (ex.id && authorId) {
    try { await api.delete(`/api/authors/${authorId}/author_examples/${ex.id}`) } catch (e) { /* ignore */ }
  }
  examples.value.splice(idx, 1)
}
</script>
